import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { PlusIcon, MapPinIcon, CalendarIcon, ArrowTrendingUpIcon } from '@heroicons/react/24/outline';
import { farmApi, alertsApi, Farm } from '../lib/api';
import { format } from 'date-fns';
import { Button, EmptyState } from '../components/ui';
import { FarmCardSkeleton } from '../components/ui/Skeleton';

function FarmCard({ farm }: { farm: Farm }) {
  // Fetch real NDVI data for this farm
  const { data: farmStats } = useQuery({
    queryKey: ['farm-stats', farm.id],
    queryFn: async () => {
      try {
        return await farmApi.getStats(farm.id);
      } catch {
        return null;
      }
    },
  });

  const ndviValue = farmStats?.latestNdvi?.meanValue ?? null;
  const ndviPercent = ndviValue !== null ? Math.round(ndviValue * 100) : 0;

  const getNdviColor = (value: number | null) => {
    if (value === null) return { bg: 'from-gray-400 to-gray-500', text: 'text-gray-500', label: 'No Data' };
    if (value < 0.3) return { bg: 'from-red-500 to-red-600', text: 'text-red-600', label: 'Poor' };
    if (value < 0.5) return { bg: 'from-amber-500 to-amber-600', text: 'text-amber-600', label: 'Fair' };
    if (value < 0.7) return { bg: 'from-kenagri-500 to-kenagri-600', text: 'text-kenagri-600', label: 'Good' };
    return { bg: 'from-emerald-500 to-emerald-600', text: 'text-emerald-600', label: 'Excellent' };
  };

  const ndviStyle = getNdviColor(ndviValue);

  return (
    <Link
      to={`/farms/${farm.id}`}
      className="group bg-white rounded-2xl border border-gray-100 shadow-card overflow-hidden hover:shadow-card-hover hover:-translate-y-1 transition-all duration-300"
    >
      {/* Header area with gradient */}
      <div className="h-36 bg-gradient-to-br from-kenagri-600 via-kenagri-500 to-kenagri-700 relative overflow-hidden">
        {/* Pattern overlay */}
        <div className="absolute inset-0 pattern-dots opacity-40" />

        {/* Decorative blur */}
        <div className="absolute -top-10 -right-10 w-32 h-32 bg-white/20 rounded-full blur-2xl" />

        {/* Icon */}
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="w-14 h-14 rounded-2xl bg-white/20 backdrop-blur-sm flex items-center justify-center border border-white/30 group-hover:scale-110 transition-transform duration-300">
            <MapPinIcon className="h-7 w-7 text-white" />
          </div>
        </div>

        {/* Badges */}
        <div className="absolute bottom-3 left-3 right-3 flex items-center justify-between">
          <span className="bg-white/95 backdrop-blur-sm rounded-full px-3 py-1 text-xs font-semibold text-kenagri-700 shadow-soft">
            {farm.areaHectares?.toFixed(1)} ha
          </span>
          <span className={`bg-white/95 backdrop-blur-sm rounded-full px-3 py-1 text-xs font-semibold ${ndviStyle.text} shadow-soft flex items-center gap-1`}>
            <ArrowTrendingUpIcon className="h-3 w-3" />
            {ndviValue !== null ? ndviValue.toFixed(2) : 'N/A'}
          </span>
        </div>
      </div>

      {/* Content */}
      <div className="p-4">
        <h3 className="font-semibold text-gray-900 group-hover:text-kenagri-600 transition-colors truncate">
          {farm.name}
        </h3>
        <p className="text-sm text-gray-500 mt-1">{farm.cropType || 'Unknown crop'}</p>

        {/* NDVI Progress */}
        <div className="mt-4">
          <div className="flex items-center justify-between text-xs mb-1.5">
            <span className="text-gray-500 font-medium">Crop Health</span>
            <span className={`font-semibold ${ndviStyle.text}`}>{ndviStyle.label}</span>
          </div>
          <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
            <div
              className={`h-full bg-gradient-to-r ${ndviStyle.bg} rounded-full transition-all duration-500`}
              style={{ width: `${ndviPercent}%` }}
            />
          </div>
        </div>

        {/* Footer */}
        {farm.plantingDate && (
          <div className="mt-4 pt-3 border-t border-gray-100 flex items-center text-xs text-gray-400">
            <CalendarIcon className="h-4 w-4 mr-1.5" />
            Planted {format(new Date(farm.plantingDate), 'MMM d, yyyy')}
          </div>
        )}
      </div>
    </Link>
  );
}

export default function Farms() {
  const { data: farms = [], isLoading } = useQuery({
    queryKey: ['farms'],
    queryFn: farmApi.list,
  });

  const { data: alertData } = useQuery({
    queryKey: ['alerts-summary'],
    queryFn: alertsApi.getSummary,
  });

  // Calculate aggregate stats from all farms
  const { data: allFarmStats } = useQuery({
    queryKey: ['all-farm-stats', farms.map(f => f.id).join(',')],
    queryFn: async () => {
      if (farms.length === 0) return { avgNdvi: null, totalAlerts: 0 };

      const statsPromises = farms.map(async (farm) => {
        try {
          const stats = await farmApi.getStats(farm.id);
          return stats?.latestNdvi?.meanValue ?? null;
        } catch {
          return null;
        }
      });

      const allStats = await Promise.all(statsPromises);
      const validStats = allStats.filter((s): s is number => s !== null);

      return {
        avgNdvi: validStats.length > 0
          ? validStats.reduce((sum, val) => sum + val, 0) / validStats.length
          : null,
      };
    },
    enabled: farms.length > 0,
  });

  const totalArea = farms.reduce((sum, f) => sum + (f.areaHectares || 0), 0);

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">My Farms</h1>
          <p className="text-gray-500 mt-1">Manage and monitor your agricultural properties</p>
        </div>
        <Link to="/farms/new">
          <Button variant="primary" size="md" leftIcon={<PlusIcon className="h-5 w-5" />}>
            Add Farm
          </Button>
        </Link>
      </div>

      {/* Content */}
      {isLoading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          <FarmCardSkeleton />
          <FarmCardSkeleton />
          <FarmCardSkeleton />
          <FarmCardSkeleton />
        </div>
      ) : farms.length > 0 ? (
        <>
          {/* Stats summary */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
            <div className="bg-gradient-to-br from-kenagri-50 to-white rounded-xl p-4 border border-kenagri-100">
              <p className="text-sm font-medium text-kenagri-600">Total Farms</p>
              <p className="text-2xl font-bold text-gray-900 mt-1">{farms.length}</p>
            </div>
            <div className="bg-gradient-to-br from-blue-50 to-white rounded-xl p-4 border border-blue-100">
              <p className="text-sm font-medium text-blue-600">Total Area</p>
              <p className="text-2xl font-bold text-gray-900 mt-1">
                {totalArea.toFixed(1)} ha
              </p>
            </div>
            <div className="bg-gradient-to-br from-purple-50 to-white rounded-xl p-4 border border-purple-100">
              <p className="text-sm font-medium text-purple-600">Avg. NDVI</p>
              <p className="text-2xl font-bold text-gray-900 mt-1">
                {allFarmStats?.avgNdvi !== null && allFarmStats?.avgNdvi !== undefined
                  ? allFarmStats.avgNdvi.toFixed(2)
                  : 'N/A'}
              </p>
            </div>
            <div className="bg-gradient-to-br from-amber-50 to-white rounded-xl p-4 border border-amber-100">
              <p className="text-sm font-medium text-amber-600">Active Alerts</p>
              <p className="text-2xl font-bold text-gray-900 mt-1">
                {alertData?.unacknowledged ?? 0}
              </p>
            </div>
          </div>

          {/* Farm grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {farms.map((farm) => (
              <FarmCard key={farm.id} farm={farm} />
            ))}
          </div>
        </>
      ) : (
        <EmptyState
          icon={MapPinIcon}
          title="No farms yet"
          description="Add your first farm to start monitoring your crops with satellite imagery and AI-powered analytics."
          action={{
            label: 'Add Your First Farm',
            href: '/farms/new',
          }}
        />
      )}
    </div>
  );
}
